import { CardContent } from "@mui/material";
import { GenericAbortSignal } from "axios";
import { MprcCard, MprcConfirmDialog, MprcFormSubHeader } from "components";
import { AutoCompleteDataModel, AutoCompleteSuggestions, MprcAutoComplete } from "components/MprcAutoComplete";
import { createAutoCompleteModel } from "components/MprcAutoComplete/AutoCompleteDataModel";
import { MprcContainer } from "components/MprcContainer";
import { TagUsage } from "generated/CoreApi_v1";
import { useSnackbar } from "notistack";
import { isCanceledError, useCoreApi } from "providers/CoreApiProvider";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from 'uuid';

////////////////////////////////////////////////////////////////////////////

interface IProps {
    placeholder?: string;
    caption: string;
    selectedItems: AutoCompleteDataModel[];
    usage: TagUsage;

    onChange: (item: AutoCompleteDataModel[]) => void;
    onNewTag?: (item: AutoCompleteDataModel) => void;
    onError: (error: any) => void;
}

////////////////////////////////////////////////////////////////////////////

/**
 * Auto complete de tags para o cadastros.
 * @param props
 */
export const TagAutoCompleteBox: React.FC<IProps> = (props) => {

    const api = useCoreApi();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const { usage, caption, placeholder, selectedItems, onChange, onNewTag, onError } = props;
    const newTag = useRef<string>("");
    const [dialog, setDialog] = useState<any>();

    ///
    const getSuggestions = (searchText: string, pageNumber: number, pageSize: number, trySelectedHistory: boolean, signal: GenericAbortSignal | undefined): Promise<AutoCompleteSuggestions<AutoCompleteDataModel>> => {
        searchText = searchText.toLowerCase();

        return new Promise<AutoCompleteSuggestions<AutoCompleteDataModel>>((resolve) => {
            api.v1.suggestions.tags({
                name: searchText,
                usage: usage,
                exceptTagIds: [],
                pageNumber: pageNumber,
                pageSize: pageSize,
            }, { signal })
                .then(result => resolve({
                    items: result.data.items as AutoCompleteDataModel[],
                    totalRows: result.data.totalRows
                }))
                .catch(e => {
                    if (!isCanceledError(e)) {
                        resolve({ items: [], totalRows: 0 });
                        onError(e);
                    }
                });
        });
    };

    ////////////////////////////////////////////////////////////////////////////
    /// Handles
    ////////////////////////////////////////////////////////////////////////////

    // Solicita confirmação incluir uma nova tag
    const handleKeyReturn = (value: string) => {
        if (value.length > 255) {
            enqueueSnackbar(t("Messages.MaxStringLimit", { ns: "shared", count: 255 }), { variant: "error" });
            return;
        }
        if (onNewTag) {
            newTag.current = value;

            setDialog(
                <MprcConfirmDialog
                    open={true}
                    message={t("Tag.TagsAutoCompleteBox.ConfirmAddMessage", { name: newTag.current })}
                    onClose={(confirm) => { if (confirm) handleNewTag(); else setDialog(null); }}
                />
            );
        }
    };

    // Inclui uma nova tag na lista e salva
    const handleNewTag = () => {
        const label = newTag.current;

        var item = createAutoCompleteModel(
            uuidv4(),
            label
        );

        if (onNewTag) onNewTag(item);

        setDialog(null);
    };

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            {dialog}
            <MprcContainer>
                <MprcCard>
                    <CardContent>
                        <MprcFormSubHeader
                            title={t("SysObjects.Tags", { ns: "shared" })}
                            subtitle={caption}
                        />
                        <MprcAutoComplete
                            margin="dense"
                            label=""
                            placeholder={placeholder}
                            selectedItems={selectedItems}
                            onGetSuggestions={getSuggestions}
                            onChange={onChange}
                            onKeyReturn={handleKeyReturn}
                        />
                    </CardContent>
                </MprcCard>
            </MprcContainer>
        </>
    );
};
